import React, { useEffect, useState } from "react";
import { Col, DatePicker, Form, FormInstance, Input, Row, Select } from "antd";
import { storage as localStorageUtil } from "utils/storage";
import dayjs from "dayjs";
import { db } from "../../../../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import { phoneValidator } from "utils/phone-validator";

const { Option } = Select;

interface LanguageType {
  id: string;
  name: string;
}

interface BasicInformationFormProps {
  form: FormInstance;
}

const BasicInformationForm: React.FC<BasicInformationFormProps> = ({ form }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateOfBirth] = useState<dayjs.Dayjs | null>(null);

  const [email, setEmail] = useState("");
  const [age, setAge] = useState("");
  const [languages, setLanguages] = useState<LanguageType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = localStorageUtil.getUserFromStorage() || {};
    if (user?.email) {
      setEmail(user.email);
      form.setFieldsValue({ email: user.email });
    }
  }, [form]);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "enumerator_languages"));
        const languageList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setLanguages(languageList);
      } catch (error) {
        console.error("Error fetching languages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  const calculateAge = (dob: string) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let personAge = today.getFullYear() - birthDate.getFullYear();

    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      personAge--;
    }

    return personAge < 15 ? "Age not valid" : personAge.toString();
  };

  const validateAge = (_: unknown, value: string): Promise<void> => {
    if (value === "" || value === "Age not valid") {
      return Promise.reject(new Error("Age must be greater than 15"));
    }
    return Promise.resolve();
  };

  const handleDobChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      const formattedDate = date.format("YYYY-MM-DD");
      const calculatedAge = calculateAge(formattedDate);
      setAge(calculatedAge);
      form.setFieldsValue({ age: calculatedAge });
    } else {
      setAge("");
      form.setFieldsValue({ age: "" });
    }
  };


  return (
    <Row gutter={[48, 32]}>
      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <Form.Item
          name="firstName"
          label="First Name"
          rules={[{ required: true, message: "First name is required" }]}
        >
          <Input
            placeholder="eg. John"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              form.setFieldsValue({ firstName: e.target.value });
            }}
          />
        </Form.Item>
      </Col>

      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <Form.Item
          name="lastName"
          label="Last Name"
          rules={[{ required: true, message: "Last name is required" }]}
        >
          <Input
            placeholder="eg. Doe"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              form.setFieldsValue({ lastName: e.target.value });
            }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <Form.Item
          name="middleName"
          label="Middle Name (Optional)"
          rules={[{ required: false }]}
        >
          <Input
            placeholder="eg. Bahadur"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              form.setFieldsValue({ middleName: e.target.value });
            }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <Form.Item
          name="dateOfBirth"
          label="Date of Birth"
          rules={[{ required: true, message: "Date of birth is required" }]}
        >
          <DatePicker
            className="w-full"
            value={dateOfBirth}
            placeholder="yy-mm-dd"
            onChange={handleDobChange}
            disabledDate={(currentDate) =>
              currentDate && currentDate.isAfter(dayjs(), "day")
            }
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <Form.Item
          name="age"
          label="Age"
          rules={[{ required: true, message: "Age is required" }, { validator: validateAge }]}
        >
          <Input value={age} placeholder="" disabled />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <Form.Item
          name="gender"
          label="Gender"
          rules={[{ required: true, message: "Gender is required" }]}
        >
          <Select
            placeholder="Select gender"
            allowClear
            value={gender}
            onChange={(value) => {
              setGender(value);
              form.setFieldsValue({ gender: value });
            }}
          >
            <Option value="male">Male</Option>
            <Option value="female">Female</Option>
            <Option value="other">Other</Option>
          </Select>
        </Form.Item>
      </Col>

      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <Form.Item
          name="phoneNumber"
          label="Phone Number"
          rules={[
            { required: true, message: "Phone number is required" },
            { validator: phoneValidator, message: "Only valid nepali numbers are allowed" },
          ]}
        >
          <Input
            placeholder="eg.9841234567"
            maxLength={10}
            value={phoneNumber}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                setPhoneNumber(value);
                form.setFieldsValue({ phoneNumber: value });
              }
            }}
          />
        </Form.Item>
      </Col>

      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <Form.Item
          name="email"
          label="Email address"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              form.setFieldsValue({ email: e.target.value });
            }}
          />
        </Form.Item>
      </Col>

      <Col xs={24} sm={24} md={24} lg={12} xl={12}>
        <Form.Item
          name="languages"
          label="Language"
          rules={[{ required: true, message: "Language is required" }]}
        >
          <Select
            mode="tags"
            tokenSeparators={[","]}
            placeholder={loading ? "Loading..." : "Select or Add Languages You Speak"}
            allowClear
            showSearch
            optionFilterProp="children"
            loading={loading}
            style={{ width: "100%" }}
          >
            {languages.map((lang) => (
              <Option key={lang.id} value={lang.name}>
                {lang.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    </Row>
  );
};

export default BasicInformationForm;
